import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";

/**
 * PaymentSuccess — polls /payments/status/{session_id} every 2s until the
 * server confirms `paid`. Never advances the UI to "paid" without confirmation.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ payment_status: "pending", status: "initiated" });
  const [ticks, setTicks] = useState(0);
  const done = useRef(false);

  useEffect(() => {
    if (!sessionId || done.current) return;
    let iv;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setState(data);
        if (data.payment_status === "paid" || data.payment_status === "failed" || data.payment_status === "expired") {
          done.current = true;
          clearInterval(iv);
        }
      } catch { /* keep polling */ }
      setTicks((t) => t + 1);
    };
    poll();
    iv = setInterval(poll, 2000);
    // Stop polling after ~60s
    const stop = setTimeout(() => { done.current = true; clearInterval(iv); }, 60000);
    return () => { clearInterval(iv); clearTimeout(stop); };
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const failed = state.payment_status === "failed" || state.payment_status === "expired";

  return (
    <div className="min-h-screen bg-background text-foreground brand-hero-bg grain relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-25 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.5), transparent 65%)" }} />
      <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full opacity-25 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.5), transparent 65%)" }} />
      <div className="glass-card p-10 w-full max-w-lg text-center relative" data-testid="payment-success-card">
        {paid ? (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" strokeWidth={1.5} />
            <h1 className="mt-4 font-display font-black text-3xl tracking-tighter">You&apos;re on air.</h1>
            <p className="mt-2 text-muted-foreground">Your subscription is active. Head back to the console — your channels are ready.</p>
            <Link
              to="/console/dashboard"
              data-testid="back-to-console-btn"
              className="mt-8 inline-flex items-center gap-2 tenant-btn rounded-full px-6 h-11 font-display font-bold text-sm"
            >
              Back to console <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        ) : failed ? (
          <>
            <div className="w-16 h-16 rounded-full bg-rose-500/15 border border-rose-400/40 mx-auto flex items-center justify-center text-rose-400 font-display font-black text-2xl">!</div>
            <h1 className="mt-4 font-display font-black text-3xl tracking-tighter">Payment didn&apos;t go through.</h1>
            <p className="mt-2 text-muted-foreground">No charge was captured. You can try again with another card.</p>
            <Link to="/pricing" data-testid="retry-checkout-btn" className="mt-8 inline-flex items-center gap-2 tenant-btn rounded-full px-6 h-11 font-display font-bold text-sm">
              Try again
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-fuchsia-400 mx-auto" strokeWidth={1.5} />
            <h1 className="mt-4 font-display font-black text-2xl tracking-tighter">Confirming your payment…</h1>
            <p className="mt-2 text-muted-foreground text-sm">This usually takes a couple of seconds. Do not close this window.</p>
            <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Attempt {ticks} · Session {sessionId?.slice(0, 10)}…
            </div>
          </>
        )}
      </div>
    </div>
  );
}
